import React, { useState, useEffect, useRef } from "react"
import Menu from "aldoo-ra/Menu"
import { PhotoIcon, EyeIcon, SwatchIcon, CodeBracketIcon, Bars3BottomLeftIcon, Squares2X2Icon } from "@heroicons/react/24/outline"
import Tooltip from "aldoo-ra/Tooltip"
import Button from "aldoo-ra/Button"
import ColorPicker from "./components/ColorPicker.jsx"
import AlignmentPicker from "./components/AlignmentPicker.jsx"
import VariablePicker from "./components/VariablePicker.jsx"
import StyleClassEditor from "./StyleClassEditor/index.jsx"
import ClassPropertyDrawer from "./components/ClassPropertyDrawer.jsx"
import usePanelManager from "./hooks/usePanelManager.js"

// Define the base toolbar menu items (without the color picker)
const baseFeaturesMenuItems = [
  {
    label: "Add Image",
    options: { action: "insertImage" },
    render: (item) => {
      return (
        <div className="flex flex-row items-center">
          <Tooltip content="Add Image" variant="dark">
            <PhotoIcon className="w-8 h-8 text-admin_text hover:text-admin_primary transition-colors" />
          </Tooltip>
        </div>
      )
    },
  },
  {
    label: "Add Button",
    options: { action: "insertButton" },
    render: (item) => {
      return (
        <div className="flex flex-row items-center">
          <Tooltip content="Add Button" variant="dark">
            <svg
              className="w-8 h-8 text-admin_text hover:text-admin_primary transition-colors"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <rect
                x="2"
                y="6"
                width="20"
                height="12"
                rx="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <text
                x="12"
                y="14"
                fontSize="6"
                textAnchor="middle"
                fill="currentColor"
                stroke="none"
              >
                BTN
              </text>
            </svg>
          </Tooltip>
        </div>
      )
    },
  },
  {
    label: "Add Link",
    options: { action: "insertLink" },
    render: (item) => {
      return (
        <div className="flex flex-row items-center">
          <Tooltip content="Add Link" variant="dark">
            <svg
              className="w-8 h-8 text-admin_text hover:text-admin_primary transition-colors"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
              />
            </svg>
          </Tooltip>
        </div>
      )
    },
  },
  {
    label: "HTML Editor",
    options: { action: "editHtmlContent" },
    render: (item) => {
      return (
        <div className="flex flex-row items-center">
          <Tooltip content="HTML Editor" variant="dark">
            <CodeBracketIcon className="w-8 h-8 text-admin_text hover:text-admin_primary transition-colors" />
          </Tooltip>
        </div>
      )
    },
  },
]

const historyMenu = {
  label: "History",
  items: [
    {
      label: "Undo",
      options: { action: "undo" },
      render: (item) => {
        return (
          <div className="flex items-center gap-2 text-admin_text hover:text-admin_primary transition-colors">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-6 h-6"
            >
              <path d="M3 7v6h6" />
              <path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13" />
            </svg>
            <span>{item.label}</span>
          </div>
        )
      },
    },
    {
      label: "Redo",
      options: { action: "redo" },
      render: (item) => {
        return (
          <div className="flex items-center gap-2 text-admin_text hover:text-admin_primary transition-colors">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-6 h-6"
            >
              <path d="M21 7v6h-6" />
              <path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13" />
            </svg>
            <span>{item.label}</span>
          </div>
        )
      },
    },
  ],
}

const fileMenu = {
  label: "File",
  items: [
    {
      label: "Preview",
      options: { action: "togglePreview" },
      render: (item) => {
        return (
          <div className="h-10 flex flex-row items-center">
            <Tooltip content="Preview Page" variant="dark">
              <EyeIcon className="w-8 h-8 text-admin_text hover:text-admin_primary transition-colors" />
            </Tooltip>
          </div>
        )
      },
    },
    {
      label: "Save",
      options: { action: "save" },
      render: (item) => {
        return (
          <Button
            variant="admin_primary"
          >
            {item.label}
          </Button>
        )
      },
    },
  ],
}

// Toolbar component
const Toolbar = ({ editor }) => {
  const [savedSelection, setSavedSelection] = useState(null)

  // Use useRef to persist isStyleEditorOpen across re-renders
  const isStyleEditorOpenRef = useRef(false)
  const [, forceUpdate] = useState({})

  const setIsStyleEditorOpen = (value) => {
    isStyleEditorOpenRef.current = value
    forceUpdate({}) // Force re-render to update UI
  }

  const isStyleEditorOpen = isStyleEditorOpenRef.current
  const [selectedElement, setSelectedElement] = useState(null)
  const [isPropertyDrawerOpen, setIsPropertyDrawerOpen] = useState(false)
  const [selectedClassForDrawer, setSelectedClassForDrawer] = useState(null)

  // Use panel manager for all pickers
  const colorPicker = usePanelManager('colorPicker')
  const alignmentPicker = usePanelManager('alignmentPicker')
  const variablePicker = usePanelManager('variablePicker')

  // Get all currently open panels from panel manager
  const getAllOpenPanels = () => {
    return [
      { id: 'colorPicker', manager: colorPicker },
      { id: 'alignmentPicker', manager: alignmentPicker },
      { id: 'variablePicker', manager: variablePicker }
    ].filter(panel => panel.manager.isOpen)
  }

  // Detect selected element for class editing
  useEffect(() => {
    const detectSelectedElement = () => {
      const selection = window.getSelection()
      if (selection.rangeCount === 0) {
        setSelectedElement(null)
        return
      }

      let element = selection.anchorNode

      // If it's a text node, get its parent element
      if (element && element.nodeType === Node.TEXT_NODE) {
        element = element.parentElement
      }

      // Make sure the element is within the editor
      const editorElement = document.querySelector('[contenteditable="true"]')
      if (element && editorElement && editorElement.contains(element)) {
        setSelectedElement(element)
      } else {
        setSelectedElement(null)
      }
    }

    // Listen for selection changes
    document.addEventListener('selectionchange', detectSelectedElement)

    // Also listen for clicks in the editor
    const editorElement = document.querySelector('[contenteditable="true"]')
    if (editorElement) {
      editorElement.addEventListener('click', detectSelectedElement)
      editorElement.addEventListener('keyup', detectSelectedElement)
    }

    return () => {
      document.removeEventListener('selectionchange', detectSelectedElement)
      if (editorElement) {
        editorElement.removeEventListener('click', detectSelectedElement)
        editorElement.removeEventListener('keyup', detectSelectedElement)
      }
    }
  }, [])



  // Global click handler for click-outside detection
  useEffect(() => {
    const handleGlobalClick = (e) => {
      const openPanels = getAllOpenPanels()

      // If no panels are open, ignore
      if (openPanels.length === 0) {
        return
      }

      // Check if click is inside ANY panel using simple element detection
      let clickInsideAnyPanel = false

      // Simple approach: check if the click target is inside a panel element
      const clickTarget = e.target

      // Check for alignment picker
      const alignmentPickerElement = clickTarget.closest('[data-alignment-picker="true"]')
      if (alignmentPickerElement) {
        clickInsideAnyPanel = true
      }

      // Check for color picker
      const colorPickerElement = clickTarget.closest('[data-color-picker="true"]')
      if (colorPickerElement) {
        clickInsideAnyPanel = true
      }

      // Check for variable picker
      const variablePickerElement = clickTarget.closest('[data-variable-picker="true"]')
      if (variablePickerElement) {
        clickInsideAnyPanel = true
      }

      // Check for class selector dropdown
      const classSelectorElement = clickTarget.closest('[data-class-selector="true"]')
      if (classSelectorElement) {
        clickInsideAnyPanel = true
      }

      // If click is outside all panels, close all open panels
      if (!clickInsideAnyPanel) {
        for (const openPanel of openPanels) {
          openPanel.manager.closePanel()
          setSavedSelection(null)
        }
      }
    }

    document.addEventListener('click', handleGlobalClick, true)
    return () => {
      document.removeEventListener('click', handleGlobalClick, true)
    }
  }, [colorPicker.isOpen, alignmentPicker.isOpen, variablePicker.isOpen, savedSelection, colorPicker, alignmentPicker, variablePicker, getAllOpenPanels])

  // Maintain selection visibility while pickers are open
  useEffect(() => {
    if ((colorPicker.isOpen || alignmentPicker.isOpen || variablePicker.isOpen) && savedSelection && savedSelection.range) {
      const maintainSelection = () => {
        const selection = window.getSelection()
        if (selection.rangeCount === 0) {
          selection.addRange(savedSelection.range.cloneRange())
        }
      }

      // Initial restore
      maintainSelection()

      // Periodically maintain selection
      const interval = setInterval(maintainSelection, 100)

      return () => clearInterval(interval)
    }
  }, [colorPicker.isOpen, alignmentPicker.isOpen, variablePicker.isOpen, savedSelection])

  const handleShowColorPicker = (event) => {

    // Prevent default behavior and losing focus
    if (event) {
      if (typeof event.preventDefault === 'function') {
        event.preventDefault()
      }
      if (typeof event.stopPropagation === 'function') {
        event.stopPropagation()
      }
    }

    // Save current selection before opening color picker
    const selection = window.getSelection()
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      setSavedSelection({
        range: range.cloneRange(),
        text: range.toString()
      })

      // Keep the selection visible
      setTimeout(() => {
        selection.removeAllRanges()
        selection.addRange(range.cloneRange())
      }, 0)
    }

    // Calculate position based on button
    let position = { x: 100, y: 100 }
    const colorButton = document.querySelector('[data-action="showColorPicker"]')
    if (colorButton) {
      const rect = colorButton.getBoundingClientRect()
      position = {
        x: rect.left,
        y: rect.bottom + 5
      }
    }

    // Switch to color picker using panel manager
    colorPicker.switchToPanel(position, { savedSelection })
  }

  const handleShowAlignmentPicker = (event) => {

    // Prevent default behavior and losing focus
    if (event) {
      if (typeof event.preventDefault === 'function') {
        event.preventDefault()
      }
      if (typeof event.stopPropagation === 'function') {
        event.stopPropagation()
      }
    }

    // Save current selection before opening alignment picker
    const selection = window.getSelection()

    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      setSavedSelection({
        range: range.cloneRange(),
        text: range.toString()
      })

      // Keep the selection visible
      setTimeout(() => {
        selection.removeAllRanges()
        selection.addRange(range.cloneRange())
      }, 0)
    } else {
      //no value selected
    }

    // Calculate position based on button
    let position = { x: 100, y: 100 }
    const alignButton = document.querySelector('[data-action="showAlignmentPicker"]')
    if (alignButton) {
      const rect = alignButton.getBoundingClientRect()
      position = {
        x: rect.left,
        y: rect.bottom + 5
      }
    }

    // Switch to alignment picker using panel manager
    alignmentPicker.switchToPanel(position, { savedSelection })
  }

  const handleShowVariablePicker = (event) => {
    // Prevent default behavior and losing focus
    if (event) {
      if (typeof event.preventDefault === 'function') {
        event.preventDefault()
      }
      if (typeof event.stopPropagation === 'function') {
        event.stopPropagation()
      }
    }

    // Save current selection before opening variable picker
    const selection = window.getSelection()
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      setSavedSelection({
        range: range.cloneRange(),
        text: range.toString()
      })

      // Keep the selection visible
      setTimeout(() => {
        selection.removeAllRanges()
        selection.addRange(range.cloneRange())
      }, 0)
    }

    // Calculate position based on button
    let position = { x: 100, y: 100 }
    const variableButton = document.querySelector('[data-action="showVariablePicker"]')
    if (variableButton) {
      const rect = variableButton.getBoundingClientRect()
      position = {
        x: rect.left,
        y: rect.bottom + 5
      }
    }

    // Switch to variable picker using panel manager
    variablePicker.switchToPanel(position, { savedSelection })
  }

  const handleColorSelect = (color) => {
    // Restore selection before applying color
    if (savedSelection && savedSelection.range) {
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(savedSelection.range)
    }

    if (color === 'transparent') {
      editor.runCommand('color.remove')
    } else {
      editor.runCommand('color.apply', { color })
    }

    colorPicker.closePanel()
    setSavedSelection(null)
  }

  const handleAlignmentSelect = (alignment) => {

    // Restore selection before applying alignment
    if (savedSelection && savedSelection.range) {
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(savedSelection.range)
    }

    // Apply alignment
    editor.runCommand('align.apply', { action: alignment })

    alignmentPicker.closePanel()
    setSavedSelection(null)
  }

  const handleVariableSelect = (variableText) => {
    // Save current state to history before making changes
    // Get the actual current content from the editor DOM
    const editorElement = document.querySelector('[contenteditable="true"]')
    const currentState = editorElement?.innerHTML || editor.resolveState?.() || ""

    // Only save to history if there's meaningful content (not just empty or whitespace)
    const hasContent = currentState.trim().length > 0 && !currentState.match(/^<p><br><\/p>$/)
    if (hasContent) {
      editor.runCommand("history.add", currentState)
    }

    let selection = window.getSelection()
    let range

    // Use saved selection if available, otherwise get current selection
    if (savedSelection && savedSelection.range) {
      range = savedSelection.range
      selection.removeAllRanges()
      selection.addRange(range)
    } else if (selection.rangeCount > 0) {
      range = selection.getRangeAt(0)
    } else {
      return // No selection available
    }

    // Create a text node with the variable
    const textNode = document.createTextNode(variableText)

    // If there's selected content, delete it first
    if (!range.collapsed) {
      range.deleteContents()
    }

    // Insert the variable text
    range.insertNode(textNode)

    // Position cursor after the inserted text
    range.setStartAfter(textNode)
    range.setEndAfter(textNode)
    range.collapse(true)

    // Update selection
    selection.removeAllRanges()
    selection.addRange(range)

    // Save the new state after the variable insertion
    setTimeout(() => {
      // Get the updated state directly from the editor DOM
      const editorElement = document.querySelector('[contenteditable="true"]')
      const newState = editorElement?.innerHTML || editor.resolveState?.() || ""
      if (newState.length >= 0) {
        editor.runCommand("history.add", newState)
      }
    }, 0)

    variablePicker.closePanel()
    setSavedSelection(null)
  }

  // Create the features menu with access to picker handlers
  const featuresMenu = {
    label: "Editor Toolbar",
    items: [
      ...baseFeaturesMenuItems.slice(0, 3), // Add Image, Add Button, Add Link
      {
        label: "Add Variable",
        options: { action: "showVariablePicker" },
        render: (item) => {
          return (
            <div
              className="flex flex-row items-center"
              data-action="showVariablePicker"
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()

                // Generic panel toggle logic
                if (variablePicker.isOpen) {
                  variablePicker.closePanel()
                  setSavedSelection(null)
                } else {
                  handleShowVariablePicker(e)
                }
              }}
              style={{ userSelect: 'none' }}
            >
              <Tooltip content="Insert Variable" variant="dark">
                <svg
                  className="w-8 h-8 text-admin_text hover:text-admin_primary transition-colors"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5.25 8.25h15m-16.5 7.5h15m-1.8-13.5-3.9 19.5m-2.1-19.5-3.9 19.5"
                  />
                </svg>
              </Tooltip>
            </div>
          )
        },
      },
      {
        label: "Text Color",
        options: { action: "showColorPicker" },
        render: (item) => {
          return (
            <div
              className="flex flex-row items-center"
              data-action="showColorPicker"
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()

                // Generic panel toggle logic
                if (colorPicker.isOpen) {
                  colorPicker.closePanel()
                  setSavedSelection(null)
                } else {
                  handleShowColorPicker(e)
                }
              }}
              style={{ userSelect: 'none' }}
            >
              <Tooltip content="Text Color" variant="dark">
                <SwatchIcon className="w-8 h-8 text-admin_text hover:text-admin_primary transition-colors" />
              </Tooltip>
            </div>
          )
        },
      },
      {
        label: "Alignment",
        options: { action: "showAlignmentPicker" },
        render: (item) => {
          return (
            <div
              className="flex flex-row items-center"
              data-action="showAlignmentPicker"
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()

                // Generic panel toggle logic
                if (alignmentPicker.isOpen) {
                  alignmentPicker.closePanel()
                  setSavedSelection(null)
                } else {
                  handleShowAlignmentPicker(e)
                }
              }}
              style={{ userSelect: 'none' }}
            >
              <Tooltip content="Alignment" variant="dark">
                <Bars3BottomLeftIcon className="w-8 h-8 text-admin_text hover:text-admin_primary transition-colors" />
              </Tooltip>
            </div>
          )
        },
      },
      baseFeaturesMenuItems[3], // HTML Editor (the last item)
      {
        label: "Edit Classes",
        options: { action: "showClassSelector" },
        render: (item) => {
          const hasElementWithClasses = selectedElement && selectedElement.className && selectedElement.className.split(/\s+/).filter(cls => cls.trim() && !cls.startsWith('ae-')).length > 0

          return (
            <div
              className="flex flex-row items-center cursor-pointer"
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIsStyleEditorOpen(true)
              }}
              style={{ userSelect: 'none' }}
            >
              <Tooltip content="CSS Classes Editor - Manage all styles" variant="dark">
                <Squares2X2Icon className="w-8 h-8 text-admin_text hover:text-admin_primary transition-colors" />
              </Tooltip>
            </div>
          )
        },
      },
    ],
  }

  // Define the action map for the toolbar
  const actionMap = {
    togglePreview: editor.togglePreview,
    insertImage: editor.insertImage,
    insertButton: editor.insertButton,
    insertLink: editor.insertLink,
    editHtmlContent: editor.editHtmlContent,
    undo: editor.undo,
    redo: editor.redo,
    save: editor.save,
  }

  //if editor.readOnly, remove the save button
  if (editor.readOnly) {
    fileMenu.items = fileMenu.items.filter(
      (item) => item.options?.action !== "save"
    )
  }

  return (
    <>
      <div
        className="relative w-full flex flex-row justify-between items-center bg-admin_paper border border-admin_bordered/50 shadow-md py-2 rounded-lg mb-3"
        style={{ 
          zIndex: 100, 
          //boxShadow: "0px 4px 14px -2px rgba(0, 0, 0, 0.2)",
      }}
      >
        <Menu
          actionMap={actionMap}
          menuData={featuresMenu}
          renderAs="hlist"
          itemWidth="150px"
        />
        <Menu
          actionMap={actionMap}
          menuData={historyMenu}
          renderAs="hlist"
          itemWidth="150px"
        />
        <Menu
          actionMap={actionMap}
          menuData={fileMenu}
          renderAs="hlist"
          itemWidth="150px"
        />
      </div>

      {/* Color Picker */}
      {colorPicker.isOpen && (
        <div
          className="fixed"
          style={{
            left: colorPicker.position.x,
            top: colorPicker.position.y,
            userSelect: 'none',
            zIndex: colorPicker.zIndex
          }}
        >
          <ColorPicker
            onColorSelect={handleColorSelect}
            onClose={() => {
              colorPicker.closePanel()
              setSavedSelection(null)
            }}
            savedSelection={savedSelection}
          />
        </div>
      )}

      {/* Alignment Picker */}
      {alignmentPicker.isOpen && (
        <div
          className="fixed"
          style={{
            left: alignmentPicker.position.x,
            top: alignmentPicker.position.y,
            userSelect: 'none',
            zIndex: alignmentPicker.zIndex
          }}
        >
          <AlignmentPicker
            onAlignmentSelect={handleAlignmentSelect}
            onClose={() => {
              alignmentPicker.closePanel()
              setSavedSelection(null)
            }}
            savedSelection={savedSelection}
          />
        </div>
      )}

      {/* Variable Picker */}
      {variablePicker.isOpen && (
        <div
          className="fixed"
          style={{
            left: variablePicker.position.x,
            top: variablePicker.position.y,
            userSelect: 'none',
            zIndex: variablePicker.zIndex
          }}
        >
          <VariablePicker
            variables={editor.variables || {}}
            onVariableSelect={handleVariableSelect}
            onClose={() => {
              variablePicker.closePanel()
              setSavedSelection(null)
            }}
          />
        </div>
      )}


      {/* Class Property Drawer */}
      <ClassPropertyDrawer
        isOpen={isPropertyDrawerOpen}
        onClose={() => {
          setIsPropertyDrawerOpen(false)
          setSelectedClassForDrawer(null)
        }}
        selectedClass={selectedClassForDrawer}
        htmlContent={(() => {
          const content = editor.getContent ? editor.getContent() : ""
          return content
        })()}
      />

      {/* Style Class Editor - Full manager for creating/deleting classes */}
      <StyleClassEditor
        isOpen={isStyleEditorOpen}
        onClose={() => setIsStyleEditorOpen(false)}
        htmlContent={(() => {
          const content = editor.getContent ? editor.getContent() : ""
          return content
        })()}
      />
    </>
  )
}

export default Toolbar
